/**
 * AI Mode — conversational view of the search page (Google "AI Mode" style).
 *
 * Entered from the AI Overview's "Show more": the first turn (question +
 * already-streamed answer + sources) seeds the thread without re-requesting,
 * and follow-up questions continue the SAME backend session (the chat API's
 * FileSessionManager keeps the conversation context server-side).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, SvgIcon } from '@ui-elements';
import { useSearchStore } from '../search-provider';
import { AIStreamingService } from '../../../../services/ai-streaming.service';
import { AICitation, AIPreviewRequest } from '../ai-preview/types';
import { renderMarkdown } from '../ai-preview/markdown';
import SourcesPanel from '../sources-panel';
import styles from './index.module.scss';

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: AICitation[];
  streaming?: boolean;
  error?: string;
}

const AIMode: React.FC = () => {
  const { aiModeSeed, aiPreviewSessionId } = useSearchStore();

  const [messages, setMessages] = useState<AIMessage[]>([]);

  // (Re)seed the thread whenever a new hand-off arrives (Show More / tab on a
  // fresh search); a cleared seed (new search) empties the thread. The
  // component stays mounted across tab switches, so resuming keeps follow-ups.
  useEffect(() => {
    if (aiModeSeed) {
      setMessages([
        { role: 'user', content: aiModeSeed.question },
        {
          role: 'assistant',
          content: aiModeSeed.answer,
          sources: aiModeSeed.sources,
        },
      ]);
    } else {
      setMessages([]);
    }
  }, [aiModeSeed]);
  const [followUp, setFollowUp] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const serviceRef = useRef<AIStreamingService | null>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Same endpoint resolution as the AI Overview (env override -> window
    // global set by the site -> staging AI API).
    const w = typeof window !== 'undefined' ? (window as any) : ({} as any);
    const aiApiBase = (
      w.AiApiBase ||
      w.RecommendationsApiBase ||
      'https://ai-api-staging.inchcape-web.au'
    ).replace(/\/$/, '');
    serviceRef.current = new AIStreamingService({
      apiEndpoint:
        process.env.REACT_APP_AI_API_ENDPOINT ||
        `${aiApiBase}/chat/generate/stream`,
      apiKey: process.env.REACT_APP_AI_API_KEY || '',
      headers: {},
      timeout: 60000,
      maxRetries: 3,
    });
    inputRef.current?.focus();
    return () => serviceRef.current?.cancelCurrentRequest();
  }, []);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const ask = useCallback(
    async (question: string) => {
      const service = serviceRef.current;
      const trimmed = question.trim();
      if (!service || !trimmed || isStreaming) return;

      setFollowUp('');
      setIsStreaming(true);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: trimmed },
        { role: 'assistant', content: '', streaming: true },
      ]);

      const updateAssistant = (patch: Partial<AIMessage>) =>
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, ...patch };
          }
          return next;
        });

      const request: AIPreviewRequest = {
        prompt: trimmed,
        // Continue the session the overview's stream ACTUALLY used (carried
        // in the seed); the store id is only a fallback.
        session_id:
          aiModeSeed?.sessionId ||
          aiPreviewSessionId ||
          (typeof window !== 'undefined'
            ? `session-${Date.now()}`
            : 'ssr-session'),
        timestamp: new Date(),
        // Stated rather than left to the API default: the backend caches one
        // agent per (session, mode), so if the overview asked for a style and
        // the follow-up relied on a default that later changed, the two would
        // land on different agents and the thread would lose its context.
        mode: 'conversational',
      };

      try {
        const response = await service.streamAIResponse(
          request,
          (chunk) => updateAssistant({ content: chunk }),
          () => undefined // final state handled below / in catch
        );
        updateAssistant({
          content: response.streamedContent || response.content,
          sources: response.metadata?.sources,
          streaming: false,
        });
      } catch {
        updateAssistant({
          streaming: false,
          error:
            'Sorry — that question could not be answered right now. Please try again.',
        });
      } finally {
        setIsStreaming(false);
        inputRef.current?.focus();
      }
    },
    [aiModeSeed, aiPreviewSessionId, isStreaming]
  );

  return (
    <div
      className={styles.aiMode}
      role="region"
      aria-label="AI Mode conversation"
      data-testid="ai-mode"
    >
      <div className={styles.thread} data-testid="ai-mode-thread">
        {messages.map((message, index) =>
          message.role === 'user' ? (
            <div key={index} className={styles.userBubble}>
              {message.content}
            </div>
          ) : (
            // Two-column layout mirroring the AI Overview: answer left,
            // sources as a right-hand link-list panel.
            <div key={index} className={styles.assistantRow}>
              <div className={styles.assistantMain}>
                <div
                  className={styles.markdown}
                  // Sanitized in renderMarkdown (DOMPurify allowlist).
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(message.content),
                  }}
                />
                {message.streaming && (
                  <span className={styles.cursor} aria-hidden="true">
                    ▌
                  </span>
                )}
                {message.error && (
                  <div className={styles.error} role="alert">
                    {message.error}
                  </div>
                )}
              </div>
              {!message.streaming &&
                message.sources &&
                message.sources.length > 0 && (
                  <SourcesPanel
                    sources={message.sources}
                    testId="ai-mode-sources"
                  />
                )}
            </div>
          )
        )}
        <div ref={threadEndRef} />
      </div>

      <form
        className={styles.askForm}
        onSubmit={(e) => {
          e.preventDefault();
          ask(followUp);
        }}
      >
        <input
          ref={inputRef}
          type="text"
          maxLength={200}
          placeholder="Ask anything about Subaru…"
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
          disabled={isStreaming}
          data-testid="ai-mode-input"
        />
        <Button
          nativeType="submit"
          type="primary"
          buttonSize="medium"
          disabled={isStreaming || !followUp.trim()}
        >
          <SvgIcon type="search" size={1.6} color="#ffffff" />
        </Button>
      </form>
      <div className={styles.disclaimer}>
        AI answers are generated from subaru.com.au content and may not cover
        everything — check the linked pages for full details.
      </div>
    </div>
  );
};

export default AIMode;
